import React, { memo, useCallback, useMemo } from 'react';
import { generatePath, NavLink, NavLinkProps, useLocation } from 'react-router-dom';

import { useCustomer } from 'common/hooks/useCustomer/useCustomer.hook';

import { routes, subroute } from 'routes/routes.constants';

type AuthProtectedLinkProps = NavLinkProps & {
  href?: string;
  skipAuthCheck?: boolean;
  disabled?: boolean;
  navigate?: unknown;
};

export const AuthProtectedLink: React.FC<React.PropsWithChildren<AuthProtectedLinkProps>> = memo(
  ({ skipAuthCheck, disabled, ...props }) => {
    const { isAuthenticated } = useCustomer();
    const { pathname } = useLocation();

    const isExternal = useMemo(() => !String(props.to).includes(subroute), [props.to]);

    const returnUrl = useMemo(
      () => (isExternal ? pathname : generatePath(String(props.to))),
      [isExternal, pathname, props.to],
    );

    const redirectUrl = useMemo(
      () => `${window.location.origin}${routes.getLoginPage()}?redirectUrl=${returnUrl}`,
      [returnUrl],
    );

    const onClick = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (disabled) return;

        window.location.assign(redirectUrl);
      },
      [disabled, redirectUrl],
    );

    const ExternalLink = useCallback(
      ({ ...rest }: NavLinkProps) => {
        // Fix a console error 'Invalid value for prop `navigate` on <a> tag.'
        if ('navigate' in rest) delete rest['navigate'];

        return (
          <a
            {...rest}
            className={typeof props.className === 'string' ? props.className : undefined}
            style={typeof props.style !== 'function' ? props.style : undefined}
          >
            {props.children}
          </a>
        );
      },
      [props.children, props.className, props.style],
    );

    const linkConfig: NavLinkProps = useMemo(
      () => ({
        ...props,
      }),
      [props],
    );

    if (isExternal) {
      linkConfig.component = ExternalLink;
    }

    if (disabled || (!isAuthenticated && !skipAuthCheck)) {
      linkConfig.onClick = onClick;
    }

    const path = useMemo(() => generatePath(String(props.to)), [props.to]);

    return (
      <NavLink {...linkConfig} to={path}>
        {props.children}
      </NavLink>
    );
  },
);
